import { useEffect, useRef, useState } from 'react';
import { Building2, Loader2, Search } from 'lucide-react';
import { chercherFournisseurs } from '../services/api';

/**
 * Saisir un fournisseur en interrogeant le répertoire.
 *
 * Le champ était libre, avec pour seules suggestions les noms déjà tapés sur
 * d'autres achats. « Brasserie du Coin », « brasserie du coin » et « Brass.
 * du coin » comptaient donc pour trois fournisseurs différents, et le
 * rattachement des achats aux fiches — qui se fait par correspondance de nom —
 * échouait sur deux d'entre eux. La fiche existait, l'achat ne la trouvait
 * pas.
 *
 * Ici, la frappe interroge le répertoire : nom, ville et SIRET. Choisir une
 * fiche écrit son nom exact, et le rattachement réussit par construction
 * plutôt que par chance d'orthographe.
 *
 * Le texte libre reste permis, et c'est nécessaire : on achète parfois une
 * fois chez quelqu'un qu'on ne reverra pas, et l'obliger à créer une fiche
 * pour trois sacs de glace ferait abandonner la saisie.
 *
 * Trois cents millisecondes avant d'interroger : en dessous, on envoie une
 * requête par lettre pour rien ; au-dessus, la liste paraît en retard sur ce
 * qu'on tape.
 */
export default function ChampFournisseur({
  valeur,
  onChange,
  placeholder = 'Fournisseur',
}: {
  valeur: string;
  onChange: (nom: string) => void;
  placeholder?: string;
}) {
  const [resultats, setResultats] = useState<
    { id: number; nom: string; categorie: string; ville: string | null; siret: string | null }[]
  >([]);
  const [ouvert, setOuvert] = useState(false);
  const [cherche, setCherche] = useState(false);
  const bloc = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const terme = valeur.trim();
    // Deux caractères : en dessous, la recherche rendrait le répertoire
    // entier, ce qui n'aide personne.
    if (terme.length < 2) {
      setResultats([]);
      return;
    }

    let annule = false;
    setCherche(true);
    const minuteur = window.setTimeout(() => {
      chercherFournisseurs(terme)
        .then((r) => {
          if (!annule) setResultats(r.resultats ?? []);
        })
        .catch(() => {
          // Le répertoire injoignable ne doit pas empêcher de saisir la
          // dépense : on retombe simplement sur le texte libre.
          if (!annule) setResultats([]);
        })
        .finally(() => {
          if (!annule) setCherche(false);
        });
    }, 300);

    return () => {
      annule = true;
      window.clearTimeout(minuteur);
    };
  }, [valeur]);

  // Un clic hors du bloc referme la liste : sans cela elle reste ouverte
  // par-dessus les champs suivants.
  useEffect(() => {
    const auClic = (e: MouseEvent) => {
      if (bloc.current && !bloc.current.contains(e.target as Node)) setOuvert(false);
    };
    document.addEventListener('mousedown', auClic);
    return () => document.removeEventListener('mousedown', auClic);
  }, []);

  const exact = resultats.some((f) => f.nom.toLowerCase() === valeur.trim().toLowerCase());

  return (
    <div className="relative" ref={bloc}>
      <input
        className="input w-full pr-8"
        placeholder={placeholder}
        value={valeur}
        onChange={(e) => {
          onChange(e.target.value);
          setOuvert(true);
        }}
        onFocus={() => setOuvert(true)}
      />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
        {cherche ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : exact ? (
          <Building2 className="w-4 h-4 text-emerald-600" />
        ) : (
          <Search className="w-4 h-4 text-gray-400" />
        )}
      </span>

      {ouvert && resultats.length > 0 && (
        <ul className="absolute z-40 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {resultats.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                onClick={() => {
                  onChange(f.nom);
                  setOuvert(false);
                }}
              >
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                  {f.nom}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  {[f.categorie, f.ville, f.siret].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Dit une fois la saisie faite, et non pendant : signaler « inconnu »
          à la deuxième lettre serait un reproche permanent. */}
      {valeur.trim().length >= 2 && !cherche && !exact && resultats.length === 0 && (
        <p className="text-[0.7rem] text-gray-500 dark:text-gray-400 mt-1">
          Aucune fiche à ce nom — la dépense s'enregistrera quand même.
        </p>
      )}
    </div>
  );
}
